import axios from 'axios'
import { importSPKI, jwtVerify } from 'jose'

const BASEURL = 'https://apim.looplex.com/case-management'
const PUBLIC_KEY = 'LS0tLS1CRUdJTiBQVUJMSUMgS0VZLS0tLS0KTUlJQ0lqQU5CZ2txaGtpRzl3MEJBUUVGQUFPQ0FnOEFNSUlDQ2dLQ0FnRUF6bzBKWThaeHZSbW9kMUFJNDhqZgo4eW90KzN4VjBkVDU4NzJudFAxdGxUazdOc1VUSUF2V2ZDVk5HR01SRFhucjF1dVVHZlJVZXFFOVhMRDd4RWR3Cmt3b1gzV1BGOE5SS2ZuUUxuNERRVDNxRXJYbklDL0N2M1VIcGxzRFgyUUJMUi9YcEoyMnQ2elJCdERiNm8zUE8KRml1N2FmNmZkSnNNaUR1dCtxdlFwUWpKNG5oU3VTSkhFTXFaQnRYQitNNU1KMWJESUJ5Y2FDK0luSW9kN3o1UAo3eHNmRU5rU1FMM3JCTmhSeWVOOHRndXVobmZzbkZZbnJMNnB6T3Y0Zjdic2ZRZitVMDVBaUd2WnRJdENoNEx3CitURXFlYlMrb1VaOWRxVnVwNmJ4b0p3dDdFK2c0Q2x3MXREc0tZaTRvR2VyUkM4RkdKYXR4aHg4ZTdaWjNGUmoKYk0zL21HTWw1QnkwM015b09mQ2Y0WnErb2tULzErTEZYek95dGNLYzRVcjBiZm1BaGhqZURVN01ab0hUUVNTMQp1TEhBWGtLaUxmaC9QM1JQdjM4b29FQkk0UUxDa2FoZzhmd2Z6UzIzampBQlV2RXJuTjQyWkIyckFPUkgxZTNwCjhDbnhGdmUyRXdWWTlyVlliY1lPdFF2VS9FNnVmeHhYeW4wNk9icUZWdnlDOTlHMXZXU3pJejRLQjlUN2I2emgKWjJ0UU5IdXNVUHB6bTVFUFBOSVBQRVM0eU95ZFR5VFk4TlZFOHFvb3V6aDRGNyt2VEVVa0Zpc05UZUNHZmZqTQp6czYyOTdJbkJPakxDclluVUl6UDRDYkwwdCtLc0psRll2SE9OR1NIQjNkTUM4SW5wQ1VwbFZaV1JBTnBCNEVoCmcvK1Z4SmVIYWdxT0J1eWhNZUhVdFY4Q0F3RUFBUT09Ci0tLS0tRU5EIFBVQkxJQyBLRVktLS0tLQo='

async function getCopilotEntraAccessToken () {
  const res = await axios({
    method: 'POST',
    url: 'https://actions.looplex.com/api/code/1F83B420-5848-11F0-8074-57094DD09A64'
  })
  return res.data.access_token
}

async function getSilentlyToken (properties, services) {
  const { stm } = services
  const memory = await stm.load('WORKFLOWS_CASE_MANAGEMENT_ACCESS_TOKEN')
  if (memory) {
    const { accessToken } = memory
    const expired = await hasExpired(accessToken)
    if (accessToken && !expired) return accessToken
  }
  const msAccessToken = await getCopilotEntraAccessToken()
  const accessToken = await exchangeAccessToken(msAccessToken, properties, services)
  await stm.save('WORKFLOWS_CASE_MANAGEMENT_ACCESS_TOKEN', { accessToken })
  return accessToken
}

async function hasExpired (jwt) {
  const pem = Buffer.from(PUBLIC_KEY, 'base64').toString('utf8')
  const jwk = await importSPKI(pem, 'RS256')
  try {
    await jwtVerify(jwt, jwk)
    return false
  } catch (e) {
    if (e.code === 'ERR_JWT_EXPIRED') return true
    throw e
  }
}

function urlEncodedFromObject (o) {
  return Object.keys(o)
    .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(o[k]))
    .join('&')
}

async function exchangeAccessToken (accessToken, properties, services) {
  const { apimSubscriptionKey } = properties
  const body = {
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
    subject_token: accessToken
  }
  const data = urlEncodedFromObject(body)
  const res = await axios({
    method: 'POST',
    url: `${BASEURL}/v1/token`,
    headers: {
      'Ocp-Apim-Subscription-Key': apimSubscriptionKey,
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data
  })
  return res.data.access_token
}

export class CaseManagement {
  constructor (config) {
    for (const prop of Object.keys(config.properties)) {
      this[prop] = config.properties[prop]
    }
    this.services = config.services
  }

  static async create (properties, services) {
    const { tenant, apimSubscriptionKey } = properties
    const accessToken = await getSilentlyToken(properties, services)
    const config = {
      properties: {
        accessToken: `Bearer ${accessToken}`,
        apimSubscriptionKey,
        tenant
      },
      services
    }
    return new CaseManagement(config)
  }

  async execute (config) {
    const requestConfig = Object.assign({
      baseURL: BASEURL,
      headers: {
        'Ocp-Apim-Subscription-Key': this.apimSubscriptionKey,
        Authorization: this.accessToken,
        'X-Looplex-Tenant': this.tenant
      }
    }, config)
    try {
      const res = await axios(requestConfig)
      return [null, res]
    } catch (e) {
      return [e, null]
    }
  }
}
